/**
 * Security utilities for input validation, sanitization, and XSS protection
 */

// Dangerous script patterns to detect and block
const DANGEROUS_PATTERNS = [
  /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
  /javascript:/gi,
  /\bon\w+\s*=/gi, // onclick, onerror, etc.
  /<iframe/gi,
  /<object/gi,
  /<embed/gi,
  /<link/gi,
  /<meta/gi,
  /<style/gi,
  /expression\s*\(/gi,
  /vbscript:/gi,
  /data:text\/html/gi,
  /eval\s*\(/gi,
  /Function\s*\(/gi,
  /setTimeout\s*\(/gi,
  /setInterval\s*\(/gi,
  /document\.(cookie|write|writeln)/gi,
  /window\.(location|open|eval)/gi,
  /\.innerHTML\s*=/gi,
  /\.outerHTML\s*=/gi,
];

// Maximum input lengths
export const MAX_LENGTHS = {
  email: 255,
  name: 100,
  description: 1000,
  password: 256,
  url: 2048,
  text: 5000,
  json: 100000,
  key: 256,
  value: 10000,
};

/**
 * Validates and cleans input without HTML-encoding.
 * Use this for input fields where the value will be sent to an API.
 * Removes dangerous script patterns but preserves the original characters.
 *
 * For displaying untrusted content as HTML, use escapeForHtml() instead.
 */
export function validateAndCleanInput(input: string | null | undefined, trim: boolean = true): string {
  if (!input || typeof input !== 'string') {
    return '';
  }

  // Remove null bytes
  let cleaned = input.replace(/\0/g, '');

  // Remove dangerous patterns (but don't HTML-encode)
  for (const pattern of DANGEROUS_PATTERNS) {
    if (pattern.test(cleaned)) {
      cleaned = cleaned.replace(pattern, '');
    }
  }
  return trim ? cleaned.trim() : cleaned;
}

/**
 * Escapes HTML entities for safe display in HTML context.
 * Use this ONLY when rendering untrusted content as raw HTML (e.g., dangerouslySetInnerHTML).
 * Note: React automatically escapes text content, so this is rarely needed.
 */
export function escapeForHtml(input: string | null | undefined): string {
  if (!input || typeof input !== 'string') {
    return '';
  }

  return input
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/\//g, '&#x2F;');
}

/**
 * @deprecated Use validateAndCleanInput() for input validation or escapeForHtml() for HTML display.
 * This function HTML-encodes input which causes issues when data is sent to APIs.
 */
export function sanitizeInput(input: string | null | undefined): string {
  if (!input || typeof input !== 'string') {
    return '';
  }

  // Remove null bytes
  let sanitized = input.replace(/\0/g, '');

  // Check for dangerous patterns
  for (const pattern of DANGEROUS_PATTERNS) {
    if (pattern.test(sanitized)) {
      // Silently block dangerous patterns without logging to console
      // (Logging in production could expose security mechanisms to attackers)
      sanitized = sanitized.replace(pattern, '');
    }
  }

  // Escape HTML entities
  sanitized = sanitized
    .replace(/&/g, '&amp;')  // Must be first to avoid double-escaping
    .replace(/</g, '&lt;')   // Prevents <script> from being interpreted as HTML
    .replace(/>/g, '&gt;')   // Prevents </script> from being interpreted as HTML
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/\//g, '&#x2F;');

  return sanitized.trim();
}

/**
 * Sanitizes URL input by removing dangerous patterns but preserving URL structure
 * Does NOT HTML-escape the input (URLs need to preserve /, :, etc.)
 */
export function sanitizeUrlInput(input: string | null | undefined): string {
  if (!input || typeof input !== 'string') {
    return '';
  }

  // Remove null bytes
  let sanitized = input.replace(/\0/g, '');

  // Check for dangerous patterns and remove them (but don't HTML-escape)
  for (const pattern of DANGEROUS_PATTERNS) {
    if (pattern.test(sanitized)) {
      // Silently block dangerous patterns without logging to console
      sanitized = sanitized.replace(pattern, '');
    }
  }

  // Trim whitespace but preserve URL structure
  return sanitized.trim();
}

/**
 * Sanitizes input but preserves JSON structure (for JSON editors)
 */
export function sanitizeJsonInput(input: string | null | undefined): string {
  if (!input || typeof input !== 'string') {
    return '';
  }

  // Remove null bytes
  let sanitized = input.replace(/\0/g, '');

  // Check for dangerous script patterns (but allow JSON structure)
  const dangerousScriptPatterns = [
    /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
    /javascript:/gi,
    /\bon\w+\s*=/gi,
    /expression\s*\(/gi,
    /vbscript:/gi,
    /eval\s*\(/gi,
    /Function\s*\(/gi,
    /setTimeout\s*\(/gi,
    /setInterval\s*\(/gi,
    /document\.(cookie|write|writeln)/gi,
    /window\.(location|open|eval)/gi,
  ];

  for (const pattern of dangerousScriptPatterns) {
    if (pattern.test(sanitized)) {
      console.warn('Potentially dangerous JSON input detected and blocked:', pattern);
      sanitized = sanitized.replace(pattern, '');
    }
  }

  return sanitized;
}

/**
 * Validates and sanitizes email address
 */
export function validateEmail(email: string | null | undefined): { valid: boolean; sanitized: string; error?: string } {
  if (!email || typeof email !== 'string') {
    return { valid: false, sanitized: '', error: 'Email is required' };
  }

  const trimmed = email.trim();
  
  if (trimmed.length === 0) {
    return { valid: false, sanitized: '', error: 'Email is required' };
  }

  if (trimmed.length > MAX_LENGTHS.email) {
    return { valid: false, sanitized: validateAndCleanInput(trimmed), error: `Email must be less than ${MAX_LENGTHS.email} characters` };
  }

  // Basic email validation regex
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(trimmed)) {
    return { valid: false, sanitized: validateAndCleanInput(trimmed), error: 'Invalid email format' };
  }

  // Check for dangerous patterns
  for (const pattern of DANGEROUS_PATTERNS) {
    if (pattern.test(trimmed)) {
      return { valid: false, sanitized: '', error: 'Email contains invalid characters' };
    }
  }

  return { valid: true, sanitized: trimmed.toLowerCase() };
}

/**
 * Common weak passwords to block
 */
const COMMON_WEAK_PASSWORDS = [
  'password',
  'password123',
  '12345678',
  '123456789',
  '1234567890',
  'qwerty123',
  'abc123456',
  'password1',
  'welcome123',
  'admin123',
  'letmein',
  'monkey',
  'dragon',
  'master',
  'sunshine',
  'princess',
  'football',
  'iloveyou',
  'trustno1',
  '123456',
  '123456',
  '1234567',
  'qwerty',
  'abc123',
  'admin',
  'welcome',
];

/**
 * Checks if password contains sequential characters (e.g., "abc", "123")
 */
function hasSequentialChars(password: string, minLength: number = 3): boolean {
  for (let i = 0; i <= password.length - minLength; i++) {
    const sequence = password.slice(i, i + minLength);
    let isSequential = true;
    
    // Check ascending sequence
    for (let j = 1; j < sequence.length; j++) {
      const prev = sequence.charCodeAt(j - 1);
      const curr = sequence.charCodeAt(j);
      if (curr !== prev + 1) {
        isSequential = false;
        break;
      }
    }
    
    if (isSequential) return true;
    
    // Check descending sequence
    isSequential = true;
    for (let j = 1; j < sequence.length; j++) {
      const prev = sequence.charCodeAt(j - 1);
      const curr = sequence.charCodeAt(j);
      if (curr !== prev - 1) {
        isSequential = false;
        break;
      }
    }
    
    if (isSequential) return true;
  }
  
  return false;
}

/**
 * Checks if password contains too many repeated characters
 */
function hasExcessiveRepeats(password: string, maxRepeats: number = 3): boolean {
  let maxCount = 1;
  let currentCount = 1;
  
  for (let i = 1; i < password.length; i++) {
    if (password[i].toLowerCase() === password[i - 1].toLowerCase()) {
      currentCount++;
      maxCount = Math.max(maxCount, currentCount);
    } else {
      currentCount = 1;
    }
  }
  
  return maxCount > maxRepeats;
}

/**
 * Validates and sanitizes password with comprehensive security policies
 */
export function validatePassword(
  password: string | null | undefined,
  options?: {
    requireUppercase?: boolean;
    requireLowercase?: boolean;
    requireNumbers?: boolean;
    requireSpecialChars?: boolean;
    minLength?: number;
    maxLength?: number;
    blockCommonPasswords?: boolean;
    blockSequentialChars?: boolean;
    blockExcessiveRepeats?: boolean;
  }
): { valid: boolean; sanitized: string; error?: string; requirements?: { [key: string]: boolean } } {
  // Default options - more flexible password requirements
  const opts = {
    requireUppercase: true,
    requireLowercase: true,
    requireNumbers: true,
    requireSpecialChars: true,
    minLength: 8,
    maxLength: MAX_LENGTHS.password,
    blockCommonPasswords: false, // More flexible - allow users more freedom
    blockSequentialChars: false, // More flexible - allow sequential patterns
    blockExcessiveRepeats: false, // More flexible - allow repeated characters
    ...options,
  };

  if (!password || typeof password !== 'string') {
    return { valid: false, sanitized: '', error: 'Password is required' };
  }

  // Check for null bytes
  if (password.includes('\0')) {
    return { valid: false, sanitized: '', error: 'Password contains invalid characters' };
  }

  // Check minimum length
  if (password.length < opts.minLength) {
    return { 
      valid: false, 
      sanitized: '', 
      error: `Password must be at least ${opts.minLength} characters long` 
    };
  }

  // Check maximum length
  if (password.length > opts.maxLength) {
    return { 
      valid: false, 
      sanitized: '', 
      error: `Password must be less than ${opts.maxLength} characters` 
    };
  }

  // Track requirements
  const requirements: { [key: string]: boolean } = {};
  const errors: string[] = [];

  // Check for uppercase letters
  const hasUppercase = /[A-Z]/.test(password);
  requirements.uppercase = hasUppercase;
  if (opts.requireUppercase && !hasUppercase) {
    errors.push('one uppercase letter');
  }

  // Check for lowercase letters
  const hasLowercase = /[a-z]/.test(password);
  requirements.lowercase = hasLowercase;
  if (opts.requireLowercase && !hasLowercase) {
    errors.push('one lowercase letter');
  }

  // Check for numbers
  const hasNumbers = /[0-9]/.test(password);
  requirements.numbers = hasNumbers;
  if (opts.requireNumbers && !hasNumbers) {
    errors.push('one number');
  }

  // Check for special characters
  const hasSpecialChars = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~`]/.test(password);
  requirements.specialChars = hasSpecialChars;
  if (opts.requireSpecialChars && !hasSpecialChars) {
    errors.push('one special character');
  }

  // Check for common weak passwords
  if (opts.blockCommonPasswords) {
    const lowerPassword = password.toLowerCase();
    if (COMMON_WEAK_PASSWORDS.some(weak => lowerPassword.includes(weak.toLowerCase()))) {
      return { 
        valid: false, 
        sanitized: '', 
        error: 'Please choose a stronger password.',
        requirements 
      };
    }
  }

  // Check for sequential characters
  if (opts.blockSequentialChars && hasSequentialChars(password)) {
    return { 
      valid: false, 
      sanitized: '', 
      error: 'Password cannot contain sequential characters',
      requirements 
    };
  }

  // Check for excessive repeated characters
  if (opts.blockExcessiveRepeats && hasExcessiveRepeats(password)) {
    return { 
      valid: false, 
      sanitized: '', 
      error: 'Password cannot contain the same character repeated more than 3 times in a row',
      requirements 
    };
  }

  // If there are missing requirements, return error
  if (errors.length > 0) {
    return { 
      valid: false, 
      sanitized: '', 
      error: `Password must contain ${errors.join(', ')}`,
      requirements 
    };
  }

  return { valid: true, sanitized: password, requirements };
}

/**
 * Checks password strength and returns a score (0-100) and level
 */
export function checkPasswordStrength(password: string | null | undefined): { 
  score: number; 
  level: 'very-weak' | 'weak' | 'fair' | 'good' | 'strong' | 'very-strong';
  feedback: string[];
} {
  if (!password || typeof password !== 'string') {
    return { score: 0, level: 'very-weak', feedback: ['Password is required'] };
  }

  let score = 0;
  const feedback: string[] = [];

  // Length scoring
  if (password.length >= 8) score += 10;
  if (password.length >= 12) score += 10;
  if (password.length >= 16) score += 10;
  if (password.length >= 20) score += 5;
  if (password.length < 8) feedback.push('Use at least 8 characters');

  // Character variety scoring
  if (/[a-z]/.test(password)) {
    score += 10;
  } else {
    feedback.push('Add lowercase letters');
  }

  if (/[A-Z]/.test(password)) {
    score += 10;
  } else {
    feedback.push('Add uppercase letters');
  }

  if (/[0-9]/.test(password)) {
    score += 10;
  } else {
    feedback.push('Add numbers');
  }

  if (/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~`]/.test(password)) {
    score += 15;
  } else {
    feedback.push('Add special characters');
  }

  // Bonus for mixed case and numbers
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score += 5;
  if (/[0-9]/.test(password) && /[a-zA-Z]/.test(password)) score += 5;

  // Penalties
  if (hasSequentialChars(password)) {
    score -= 15;
    feedback.push('Avoid sequential characters');
  }

  if (hasExcessiveRepeats(password)) {
    score -= 10;
    feedback.push('Avoid repeating characters');
  }

  const lowerPassword = password.toLowerCase();
  if (COMMON_WEAK_PASSWORDS.some(weak => lowerPassword.includes(weak.toLowerCase()))) {
    score -= 20;
    feedback.push('Avoid common passwords');
  }

  // Ensure score is between 0 and 100
  score = Math.max(0, Math.min(100, score));

  // Determine level
  let level: 'very-weak' | 'weak' | 'fair' | 'good' | 'strong' | 'very-strong';
  if (score < 30) level = 'very-weak';
  else if (score < 50) level = 'weak';
  else if (score < 70) level = 'fair';
  else if (score < 85) level = 'good';
  else if (score < 95) level = 'strong';
  else level = 'very-strong';

  // Positive feedback for strong passwords
  if (score >= 70 && feedback.length === 0) {
    feedback.push('Strong password!');
  }

  return { score, level, feedback };
}

/**
 * Gets password requirements as a list for UI display
 * Returns only the essential requirements (more flexible - no restrictions on patterns)
 */
export function getPasswordRequirements(): Array<{ 
  label: string; 
  test: (password: string) => boolean;
}> {
  return [
    {
      label: 'At least 8 characters long',
      test: (pwd) => pwd.length >= 8,
    },
    {
      label: 'Contains at least one uppercase letter',
      test: (pwd) => /[A-Z]/.test(pwd),
    },
    {
      label: 'Contains at least one lowercase letter',
      test: (pwd) => /[a-z]/.test(pwd),
    },
    {
      label: 'Contains at least one number',
      test: (pwd) => /[0-9]/.test(pwd),
    },
    {
      label: 'Contains at least one special character',
      test: (pwd) => /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~`]/.test(pwd),
    },
  ];
}

/**
 * Validates and sanitizes a name field
 */
export function validateName(name: string | null | undefined, fieldName: string = 'Name'): { valid: boolean; sanitized: string; error?: string } {
  if (!name || typeof name !== 'string') {
    return { valid: false, sanitized: '', error: `${fieldName} is required` };
  }

  const trimmed = name.trim();
  
  if (trimmed.length === 0) {
    return { valid: false, sanitized: '', error: `${fieldName} is required` };
  }

  if (trimmed.length > MAX_LENGTHS.name) {
    return { valid: false, sanitized: validateAndCleanInput(trimmed), error: `${fieldName} must be less than ${MAX_LENGTHS.name} characters` };
  }

  // Check for dangerous patterns
  for (const pattern of DANGEROUS_PATTERNS) {
    if (pattern.test(trimmed)) {
      return { valid: false, sanitized: '', error: `${fieldName} contains invalid characters` };
    }
  }

  return { valid: true, sanitized: trimmed };
}

/**
 * Validates and sanitizes a URL
 */
export function validateUrl(url: string | null | undefined): { valid: boolean; sanitized: string; error?: string } {
  if (!url || typeof url !== 'string') {
    return { valid: false, sanitized: '', error: 'URL is required' };
  }

  const trimmed = url.trim();
  
  if (trimmed.length === 0) {
    return { valid: false, sanitized: '', error: 'URL is required' };
  }

  if (trimmed.length > MAX_LENGTHS.url) {
    return { valid: false, sanitized: sanitizeUrlInput(trimmed), error: `URL must be less than ${MAX_LENGTHS.url} characters` };
  }

  // Check for dangerous patterns
  for (const pattern of DANGEROUS_PATTERNS) {
    if (pattern.test(trimmed)) {
      return { valid: false, sanitized: '', error: 'URL contains invalid characters' };
    }
  }

  // Basic URL validation - allow both http:// and https://
  // Check if URL starts with http:// or https://
  const httpPattern = /^https?:\/\//i;
  if (!httpPattern.test(trimmed)) {
    return { valid: false, sanitized: sanitizeUrlInput(trimmed), error: 'URL must start with http:// or https://' };
  }

  // Validate URL format using URL constructor
  try {
    const urlObj = new URL(trimmed);
    // Explicitly allow both http:// and https:// protocols
    if (urlObj.protocol !== 'http:' && urlObj.protocol !== 'https:') {
      return { valid: false, sanitized: sanitizeUrlInput(trimmed), error: 'URL must use http:// or https://' };
    }
  } catch {
    // If URL constructor fails but pattern matches, it might be a malformed URL
    // Still allow it if it matches the basic pattern (user can fix format issues)
    // This allows http://localhost, http://127.0.0.1, etc. even if URL constructor is strict
    return { valid: true, sanitized: trimmed };
  }

  return { valid: true, sanitized: trimmed };
}

/**
 * Validates and sanitizes a key (for settings, tags, etc.)
 */
export function validateKey(key: string | null | undefined): { valid: boolean; sanitized: string; error?: string } {
  if (!key || typeof key !== 'string') {
    return { valid: false, sanitized: '', error: 'Key is required' };
  }

  const trimmed = key.trim();
  
  if (trimmed.length === 0) {
    return { valid: false, sanitized: '', error: 'Key is required' };
  }

  if (trimmed.length > MAX_LENGTHS.key) {
    return { valid: false, sanitized: validateAndCleanInput(trimmed), error: `Key must be less than ${MAX_LENGTHS.key} characters` };
  }

  // Keys should only contain alphanumeric, dots, underscores, and hyphens
  if (!/^[a-zA-Z0-9._-]+$/.test(trimmed)) {
    return { valid: false, sanitized: validateAndCleanInput(trimmed), error: 'Key can only contain letters, numbers, dots, underscores, and hyphens' };
  }

  return { valid: true, sanitized: trimmed };
}

/**
 * Validates text input with length limit
 */
export function validateText(text: string | null | undefined, maxLength: number = MAX_LENGTHS.text, fieldName: string = 'Text'): { valid: boolean; sanitized: string; error?: string } {
  if (!text || typeof text !== 'string') {
    return { valid: true, sanitized: '' }; // Text fields can be empty
  }

  const trimmed = text.trim();
  
  if (trimmed.length > maxLength) {
    return { valid: false, sanitized: validateAndCleanInput(trimmed), error: `${fieldName} must be less than ${maxLength} characters` };
  }

  // Check for dangerous patterns
  for (const pattern of DANGEROUS_PATTERNS) {
    if (pattern.test(trimmed)) {
      return { valid: false, sanitized: '', error: `${fieldName} contains invalid characters` };
    }
  }

  return { valid: true, sanitized: trimmed };
}

/**
 * Validates a date input (YYYY-MM-DD format)
 * Note: Dates should NOT be HTML-escaped as they are used in date input fields
 */
export function validateDate(date: string | null | undefined): { valid: boolean; sanitized: string; error?: string } {
  if (!date || typeof date !== 'string') {
    return { valid: true, sanitized: '' }; // Date fields can be empty
  }

  const trimmed = date.trim();
  
  if (trimmed.length === 0) {
    return { valid: true, sanitized: '' }; // Empty date is valid
  }

  // Validate date format (YYYY-MM-DD)
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!dateRegex.test(trimmed)) {
    return { valid: false, sanitized: trimmed, error: 'Invalid date format. Please use YYYY-MM-DD format' };
  }

  // Validate that it's a valid date
  const dateObj = new Date(trimmed);
  if (isNaN(dateObj.getTime())) {
    return { valid: false, sanitized: trimmed, error: 'Invalid date. Please enter a valid date' };
  }

  // Check if the date string matches the parsed date (catches invalid dates like 2024-13-45)
  const [year, month, day] = trimmed.split('-').map(Number);
  if (dateObj.getFullYear() !== year || dateObj.getMonth() + 1 !== month || dateObj.getDate() !== day) {
    return { valid: false, sanitized: trimmed, error: 'Invalid date. Please enter a valid date' };
  }

  // Check for dangerous patterns (but don't HTML-escape dates)
  for (const pattern of DANGEROUS_PATTERNS) {
    if (pattern.test(trimmed)) {
      return { valid: false, sanitized: '', error: 'Date contains invalid characters' };
    }
  }

  return { valid: true, sanitized: trimmed };
}

/**
 * Validates time strings (HH:MM format from <input type="time">)
 */
export function validateTime(time: string | null | undefined): { valid: boolean; sanitized: string; error?: string } {
  if (!time || typeof time !== 'string') {
    return { valid: true, sanitized: '' }; // Time fields can be empty
  }

  const trimmed = time.trim();

  if (trimmed.length === 0) {
    return { valid: true, sanitized: '' }; // Empty time is valid
  }

  // Validate time format (HH:MM)
  const timeRegex = /^([01]\d|2[0-3]):([0-5]\d)$/;
  if (!timeRegex.test(trimmed)) {
    return { valid: false, sanitized: trimmed, error: 'Invalid time format. Please use HH:MM format' };
  }

  return { valid: true, sanitized: trimmed };
}

/**
 * Safely parses JSON with error handling
 */
export function safeJsonParse<T = any>(jsonString: string | null | undefined, defaultValue: T = {} as T): { success: boolean; data: T; error?: string } {
  if (!jsonString || typeof jsonString !== 'string') {
    return { success: false, data: defaultValue, error: 'Invalid JSON string' };
  }

  // Sanitize before parsing
  const sanitized = sanitizeJsonInput(jsonString);

  try {
    const parsed = JSON.parse(sanitized);
    return { success: true, data: parsed };
  } catch (error: any) {
    return { success: false, data: defaultValue, error: error.message || 'Invalid JSON format' };
  }
}

/**
 * Safely stringifies JSON
 */
export function safeJsonStringify(obj: any, defaultValue: string = '{}'): string {
  try {
    return JSON.stringify(obj, null, 2);
  } catch (error) {
    console.error('JSON stringify error:', error);
    return defaultValue;
  }
}

/**
 * Sanitizes URL parameters
 */
export function sanitizeUrlParam(param: string | null | undefined): string {
  if (!param || typeof param !== 'string') {
    return '';
  }

  // Decode URI component first
  try {
    const decoded = decodeURIComponent(param);
    return validateAndCleanInput(decoded);
  } catch {
    // If decoding fails, just sanitize the original
    return validateAndCleanInput(param);
  }
}

/**
 * Validates tool name (alphanumeric and underscores only)
 */
export function validateToolName(name: string | null | undefined): { valid: boolean; sanitized: string; error?: string } {
  if (!name || typeof name !== 'string') {
    return { valid: false, sanitized: '', error: 'Tool name is required' };
  }

  const trimmed = name.trim();
  
  if (trimmed.length === 0) {
    return { valid: false, sanitized: '', error: 'Tool name is required' };
  }

  if (trimmed.length > MAX_LENGTHS.name) {
    return { valid: false, sanitized: validateAndCleanInput(trimmed), error: `Tool name must be less than ${MAX_LENGTHS.name} characters` };
  }

  // Tool names should only contain alphanumeric and underscores
  if (!/^[a-zA-Z0-9_]+$/.test(trimmed)) {
    return { valid: false, sanitized: validateAndCleanInput(trimmed), error: 'Tool name must contain only letters, numbers, and underscores' };
  }

  return { valid: true, sanitized: trimmed };
}

/**
 * Checks if a string contains potentially malicious content
 */
export function containsMaliciousContent(input: string | null | undefined): boolean {
  if (!input || typeof input !== 'string') {
    return false;
  }

  for (const pattern of DANGEROUS_PATTERNS) {
    if (pattern.test(input)) {
      return true;
    }
  }

  return false;
}

/**
 * Sanitizes object recursively (for API payloads)
 */
export function sanitizeObject<T>(obj: T): T {
  if (!obj || typeof obj !== 'object') {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(item => 
      typeof item === 'string' ? validateAndCleanInput(item) : 
      typeof item === 'object' && item !== null ? sanitizeObject(item) : 
      item
    ) as unknown as T;
  }

  const sanitized = { ...obj } as Record<string, any>;
  for (const key in sanitized) {
    if (typeof sanitized[key] === 'string') {
      sanitized[key] = validateAndCleanInput(sanitized[key]);
    } else if (typeof sanitized[key] === 'object' && sanitized[key] !== null) {
      sanitized[key] = sanitizeObject(sanitized[key]);
    }
  }

  return sanitized as T;
}

export function isValidRedirectUrl(url: string): boolean {
  if (!url) return false;

  const trimmed = url.trim();
  const lower = trimmed.toLowerCase();

  // Common dangerous prefixes we never allow
  const dangerous = ['javascript:', 'data:', 'vbscript:', '<script'];
  if (dangerous.some(pattern => lower.includes(pattern))) {
    return false;
  }

  // 1) Allow safe relative URLs (same-domain redirects)
  if (trimmed.startsWith('/') && !trimmed.startsWith('//')) {
    return true;
  }

  // 2) Allow specific trusted absolute domains (e.g. Claude MCP callback)
  try {
    const parsed = new URL(trimmed);

    // Only http/https
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      return false;
    }


    //todo -fix it
    // const allowedHosts = new Set<string>([
    //   ...(import.meta.env.VITE_ALLOWS_HOSTS?.split(',') || []),
    // ]);
    // if (!allowedHosts.has(parsed.hostname)) {
    //   return false;
    // }

    return true;
  } catch {
    // Not a valid absolute URL and not a safe relative one
    return false;
  }
}